import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Hammer, DoorOpen, Droplets, Zap, Paintbrush, ThermometerSun, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const fadeIn = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const services = [
  {
    icon: Hammer, title: "Maçonnerie", subtitle: "Gros œuvre & structure",
    description: "Que ce soit pour ouvrir un mur porteur ou restructurer totalement votre appartement, nous assurons la solidité et la pérennité de votre bâtiment avec une expertise rigoureuse.",
    features: ["Ouverture de murs et pose d'IPN", "Restructuration complète", "Création de cloisons", "Travaux de gros œuvre"],
  },
  {
    icon: DoorOpen, title: "Menuiserie", subtitle: "Aménagements & boiseries",
    description: "Nous allions le respect des boiseries anciennes à la création d'aménagements modernes. Notre objectif est d'optimiser chaque mètre carré tout en apportant une touche d'élégance.",
    features: ["Dressings et rangements sur-mesure", "Restauration de boiseries de style", "Pose de parquets (massif, Hongrie)", "Portes et fenêtres isolantes"],
  },
  {
    icon: Droplets, title: "Plomberie", subtitle: "Cuisines & salles de bains",
    description: "De la rénovation invisible des réseaux à la création de votre pièce d'eau idéale, nous garantissons un travail soigné, durable et parfaitement étanche.",
    features: ["Création clé en main de SDB/Cuisine", "Rénovation complète des réseaux", "Remplacement chaudières et ballons", "Robinetterie haut de gamme"],
  },
  {
    icon: Zap, title: "Electricité", subtitle: "Sécurité & confort moderne",
    description: "Nous sécurisons votre habitat tout en intégrant les solutions technologiques d'aujourd'hui pour un éclairage et un confort parfaitement maîtrisés.",
    features: ["Mise en sécurité totale de l'habitat", "Remplacement de tableaux électriques", "Installation d'éclairages LED", "Solutions domotiques simples"],
  },
  {
    icon: Paintbrush, title: "Peinture", subtitle: "Décoration & finitions",
    description: "Nos peintres préparent minutieusement chaque support pour obtenir un résultat impeccable. Nous maîtrisons les finitions les plus exigeantes pour magnifier votre décoration.",
    features: ["Peinture haut de gamme (mate, satin)", "Restauration de moulures et corniches", "Enduits décoratifs et finitions", "Préparation rigoureuse des fonds"],
  },
  {
    icon: ThermometerSun, title: "Isolation", subtitle: "Confort thermique & acoustique",
    description: "Nous adaptons nos solutions aux contraintes spécifiques des immeubles parisiens pour vous protéger efficacement du froid et des nuisances sonores urbaines.",
    features: ["Isolation thermique par l'intérieur", "Solutions acoustiques performantes", "Matériaux isolants et durables", "Réduction des pertes de chaleur"],
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-20">
      <section className="py-24 md:py-32 bg-[#E5E0D8]/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeIn} className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium">Nos services</motion.p>
            <motion.h1 variants={fadeIn} className="font-heading text-5xl md:text-7xl font-medium tracking-tight text-[#2C2C2C] mt-4">Votre rénovation,<br />notre métier</motion.h1>
            <motion.div variants={fadeIn} className="section-divider mt-8 mb-8" />
            <motion.p variants={fadeIn} className="text-lg md:text-xl font-light leading-relaxed text-[#2C2C2C]/70 max-w-2xl">
              Un interlocuteur unique pour l'ensemble de vos travaux. De la conception à la réception, 2BW BAT coordonne chaque corps de métier pour vous garantir un résultat impeccable, dans les délais convenus.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.div variants={fadeIn} className="text-center mb-20">
              <p className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium">Notre méthode</p>
              <h2 className="font-heading text-4xl md:text-5xl font-normal tracking-tight text-[#2C2C2C] mt-4">Clé en main, de A à Z</h2>
              <div className="section-divider mx-auto mt-6" />
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Diagnostic", desc: "Visite technique, analyse des contraintes, étude de faisabilité." },
                { step: "02", title: "Conception", desc: "Plans détaillés, choix des matériaux, devis transparent." },
                { step: "03", title: "Réalisation", desc: "Coordination des corps de métier, suivi de chantier rigoureux." },
                { step: "04", title: "Réception", desc: "Contrôle qualité, procès-verbal de réception, garantie décennale." },
              ].map((item, i) => (
                <motion.div key={i} variants={fadeIn} className="text-center md:text-left">
                  <span className="font-accent text-5xl text-[#C5A065]/30 italic">{item.step}</span>
                  <h3 className="font-heading text-xl font-medium text-[#2C2C2C] mt-2 mb-3">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-[#8A8A8A]">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Separator className="max-w-7xl mx-auto" />

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {services.map((service, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={fadeIn}>
                <Card className="bg-white border border-border/50 rounded-none shadow-none overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                      <div className="p-8 lg:p-12 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-border/50">
                        <service.icon className="w-10 h-10 text-[#C5A065] stroke-[1.5] mb-6" />
                        <h3 className="font-heading text-3xl font-medium text-[#2C2C2C]">{service.title}</h3>
                        <p className="text-sm tracking-widest uppercase text-[#C5A065] mt-1">{service.subtitle}</p>
                      </div>
                      <div className="p-8 lg:p-12 lg:col-span-2">
                        <p className="text-base leading-relaxed text-[#2C2C2C]/70 mb-8">{service.description}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {service.features.map((feat, j) => (
                            <div key={j} className="flex items-center gap-3">
                              <CheckCircle2 className="w-4 h-4 text-[#C5A065] shrink-0" />
                              <span className="text-sm text-[#2C2C2C]/80">{feat}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeIn} className="font-heading text-4xl md:text-5xl font-normal text-white tracking-tight">Un projet en tête ?</motion.h2>
            <motion.p variants={fadeIn} className="text-base text-white/60 max-w-lg mx-auto mt-6 mb-12">
              Bénéficiez d'un diagnostic gratuit et d'un devis détaillé pour votre projet de rénovation.
            </motion.p>
            <motion.div variants={fadeIn}>
              <Link to="/contact">
                <Button className="group bg-[#C5A065] text-white hover:bg-[#C5A065]/90 px-10 py-6 text-sm tracking-widest uppercase rounded-none">
                  Demander un devis
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
